/**
 * Where an article has got to, and where to send somebody who comes back.
 *
 * An article is made in four moves -条件 → 構成 → 本文 → 仕上げ - and until now
 * the only way back into one was the editor, whatever state it was in. Somebody
 * who saved after choosing headings and came back the next day landed on an empty
 * body with no indication that the next thing to do was write it.
 *
 * DERIVED, NEVER STORED. A stage column would be a second copy of facts the rows
 * already carry - are there sections, do they have text, does the article have a
 * body - and the two would part company the first time somebody deleted a
 * section or pasted a body in by hand. Reading it from the article costs nothing
 * and cannot go stale.
 *
 * `status` is a different question and stays its own field: 下書き / 要確認 /
 * 承認済み is where an article is in the CLINIC's process, and an approved article
 * is still at the 仕上げ stage of being made.
 */
/*
 * The shape this module needs, declared here rather than imported from api.ts.
 *
 * A type-only import still puts that module into the compilation, and api.ts is
 * browser code - `window` and all - while this file is also compiled by the
 * server config for its unit test. Two fields is a cheaper thing to state twice
 * than a DOM dependency is to carry.
 */
interface SectionLike {
  body_md: string;
}

export type Stage = 'brief' | 'plan' | 'write' | 'polish';

/*
 * 条件 and 仕上げ live on the same screen, and that is why 条件 needs the anchor.
 *
 * Both are the article editor: the settings at the top, the body below them. As
 * two identical URLs the first step was unreachable - pressing 条件 navigated to
 * the page you were already on, landed at the top of the body, and looked like a
 * dead control. The fragment scrolls to the settings, which is what step 1 IS.
 */
export const STAGES: Array<{ id: Stage; label: string; path: (id: string) => string }> = [
  { id: 'brief', label: '条件', path: (id) => `/articles/${id}#brief` },
  { id: 'plan', label: '構成', path: (id) => `/articles/${id}/plan` },
  { id: 'write', label: '本文', path: (id) => `/articles/${id}/write` },
  { id: 'polish', label: '仕上げ', path: (id) => `/articles/${id}` },
];

/** What a person coming back to this article should do next. */
export interface StageInput {
  /** Present on a loaded article; the list sends `has_body` instead. */
  body_md?: string;
  has_body?: boolean;
  sections?: SectionLike[];
  section_count?: number;
  drafted_count?: number;
  headings?: string[];
}

export function stageOf(article: StageInput): Stage {
  const sections = article.sections ?? [];
  const total = article.section_count ?? sections.length;
  const hasOutline = total > 0 || (article.headings?.length ?? 0) > 0;

  // A body is a body however it got there - generated, pasted, or written by
  // hand - so it is the strongest signal and is read first.
  if (article.has_body ?? (article.body_md ?? '') !== '') return 'polish';
  return hasOutline ? 'write' : 'plan';
}

/** One line saying what is left, for the list and the header. */
export function stageSummary(article: StageInput): {
  stage: Stage;
  label: string;
  next: string;
} {
  const stage = stageOf(article);
  const sections = article.sections ?? [];
  const total = article.section_count ?? sections.length;
  const drafted =
    article.drafted_count ?? sections.filter((section) => section.body_md !== '').length;

  if (stage === 'polish') return { stage, label: '仕上げ', next: '編集する' };
  if (stage === 'write') {
    return {
      stage,
      label: drafted > 0 ? `本文 ${drafted}/${total}節` : '本文',
      next: drafted > 0 ? '続きを書く' : '本文を書く',
    };
  }
  return { stage, label: '構成', next: '構成を作る' };
}
